//================================================================
//  Component: CheckBox ~ Child component of 'AdminUserTable.js'
//================================================================

//  Purpose: Checkboxes that add roles to users within each roles document

//  Properties:
//    - documentid = {string, Firestore document id}
//    - userObject = {object, current user object}
//    - roleId = {string, roleId that the checkbox is representing. e.g. delete, write, create, download & read}

//  Example:
//    <AdminUserTableCheckBox
//      documentid={'agreements'}
//      userObject={userObject}
//      roleId={'delete'}
//    ></AdminUserTableCheckBox>    

//================================================================

//Libraries
import React, { useContext, useState } from 'react';

//Contexts
import { GetUser, GetRoles, SetToast } from '../../../../../../Library/GlobalContexts';

//Functions
import WriteDocument from '../../../../../../Library/WriteDocument';

//Images
import successIcon from '../../../../../../Components/Images/Icon_Save_Teal.svg';
import pendingIcon from '../../../../../../Components/Images/Icon_LoadingFile_Grey.gif';
import errorIcon from '../../../../../../Components/Images/Icon_Error_Red.svg';


export default function AdminUserTableCheckBox({
    documentid,
    userObject,
    roleId,
}) {

    //------------------------------------------------------
    //  useContexts & React Router
    //------------------------------------------------------

    const getUser = useContext(GetUser);
    const getRoles = useContext(GetRoles);
    const setToast = useContext(SetToast);

    //------------------------------------------------------
    //  useState
    //------------------------------------------------------

    // Used to determine the status of the pane > 'onload', 'pending', 'success' & 'error'
    const [checkboxStatus, setCheckboxStatus] = useState('onload');

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Handle to update user role changes
    function UpdateUsersRole(checkedValue) {

        if (typeof (checkedValue) !== 'boolean') throw Error('AdminUserTable - Invalid roleId option provided, this function ONLY supports "boolens"');

        setCheckboxStatus('pending');

        // Select the collect roles document
        const users = getRoles[documentid];

        // Update roleId with checked value
        users[userObject.emailaddress][roleId] = checkedValue;

        // ----------------------------------------
        // Update all roles that are IMPACTED
        // ----------------------------------------

        // delete
        if (roleId === 'delete') {

            if (checkedValue === false) {

                users[userObject.emailaddress]['delete'] = false;
                users[userObject.emailaddress]['write'] = true;
                users[userObject.emailaddress]['create'] = true;
                users[userObject.emailaddress]['download'] = true;
                users[userObject.emailaddress]['read'] = true;

            } else {

                users[userObject.emailaddress]['delete'] = true;
                users[userObject.emailaddress]['write'] = true;
                users[userObject.emailaddress]['create'] = true;
                users[userObject.emailaddress]['download'] = true;
                users[userObject.emailaddress]['read'] = true;

            }

        }

        // write
        else if (roleId === 'write') {

            if (checkedValue === false) {

                users[userObject.emailaddress]['delete'] = false;
                users[userObject.emailaddress]['write'] = false;
                users[userObject.emailaddress]['create'] = true;
                users[userObject.emailaddress]['download'] = true;
                users[userObject.emailaddress]['read'] = true;

            } else {

                users[userObject.emailaddress]['delete'] = false;
                users[userObject.emailaddress]['write'] = true;
                users[userObject.emailaddress]['create'] = true;
                users[userObject.emailaddress]['download'] = true;
                users[userObject.emailaddress]['read'] = true;

            }

        }

        // create
        else if (roleId === 'create') {

            if (checkedValue === false) {

                users[userObject.emailaddress]['delete'] = false;
                users[userObject.emailaddress]['write'] = false;
                users[userObject.emailaddress]['create'] = false;
                users[userObject.emailaddress]['download'] = true;
                users[userObject.emailaddress]['read'] = true;

            } else {

                users[userObject.emailaddress]['delete'] = false;
                users[userObject.emailaddress]['write'] = false;
                users[userObject.emailaddress]['create'] = true;
                users[userObject.emailaddress]['download'] = true;
                users[userObject.emailaddress]['read'] = true;

            }

        }

        // download
        else if (roleId === 'download') {

            if (checkedValue === false) {

                users[userObject.emailaddress]['delete'] = false;
                users[userObject.emailaddress]['write'] = false;
                users[userObject.emailaddress]['create'] = false;
                users[userObject.emailaddress]['download'] = false;
                users[userObject.emailaddress]['read'] = true;

            } else {

                users[userObject.emailaddress]['delete'] = false;
                users[userObject.emailaddress]['write'] = false;
                users[userObject.emailaddress]['create'] = false;
                users[userObject.emailaddress]['download'] = true;
                users[userObject.emailaddress]['read'] = true;

            }

        }

        // read
        else {

            if (checkedValue === false) {

                users[userObject.emailaddress]['delete'] = false;
                users[userObject.emailaddress]['write'] = false;
                users[userObject.emailaddress]['create'] = false;
                users[userObject.emailaddress]['download'] = false;
                users[userObject.emailaddress]['read'] = false;

            } else {

                users[userObject.emailaddress]['delete'] = false;
                users[userObject.emailaddress]['write'] = false;
                users[userObject.emailaddress]['create'] = false;
                users[userObject.emailaddress]['download'] = false;
                users[userObject.emailaddress]['read'] = true;

            }

        }

        // ----------------------------------------
        // Update Firestore
        // ----------------------------------------

        // Write over the WHOLE document!
        const document = {
            'collectionid': documentid,
            'lastmodifieddate': new Date(),
            'lastmodifiedby': {
                'email': getUser.emailaddress,
                'givenname': getUser.givenname,
                'surname': getUser.surname,
            },
            'users': users,
        };

        WriteDocument('roles', documentid, document, false).then(() => {

            setCheckboxStatus('success');

        }).catch((error) => {

            console.log('error', error);
            setCheckboxStatus('error');

            // Set error on toast
            setToast({
                'type': 'error',
                'message': <>Something went wrong. Please try again or <a href='https://digitalservices.lendlease.com/help' target='_blank' rel='noopener noreferrer'>get help.</a></>,
            });

        });

    }

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    //====================================================
    //  Pending
    //====================================================

    if (checkboxStatus === 'pending') {

        return (

            <div className='flex flex-row gap-2'>

                {/* Check box */}
                <label style={{ marginLeft: '8px', marginBottom: '25px' }} className='Checkbox-Container'>
                    <input
                        type='checkbox'
                        checked={userObject[roleId]}
                        defaultValue={userObject[roleId]}
                        disabled
                    ></input>
                    <span className='Checkbox cursor-not-allowed'></span>
                </label>

                {/* Pending Icon */}
                <img className='w-[25px]' src={pendingIcon} alt='pending-icon'></img>

            </div>

        )

    }

    // ---------------------------------------------------
    //  Onload
    // ---------------------------------------------------

    else if (checkboxStatus === 'onload') {

        return (

            <label style={{ marginLeft: '8px', marginBottom: '25px' }} className='Checkbox-Container'>
                <input
                    type='checkbox'
                    defaultValue={userObject[roleId]}
                    checked={userObject[roleId]}
                    onChange={(e) => UpdateUsersRole(!userObject[roleId])}
                ></input>
                <span className='Checkbox'></span>
            </label>

        )

    }

    // ---------------------------------------------------
    //  Success
    // ---------------------------------------------------

    else if (checkboxStatus === 'success') {

        return (

            <div className='flex flex-row gap-2'>

                {/* Check box */}
                <label style={{ marginLeft: '8px', marginBottom: '25px' }} className='Checkbox-Container'>
                    <input
                        type='checkbox'
                        defaultValue={userObject[roleId]}
                        checked={userObject[roleId]}
                        onChange={(e) => UpdateUsersRole(!userObject[roleId])}
                    ></input>
                    <span className='Checkbox'></span>
                </label>

                {/* Success Icon */}
                <img className='LiveField-Success w-[25px]' src={successIcon} alt='success-icon'></img>

            </div>

        )

    }

    // ---------------------------------------------------
    //  Error
    // ---------------------------------------------------

    else {

        return (

            <div className='flex flex-row gap-2'>

                {/* Check box */}
                <label style={{ marginLeft: '8px', marginBottom: '25px' }} className='Checkbox-Container'>
                    <input
                        type='checkbox'
                        defaultValue={userObject[roleId]}
                        checked={userObject[roleId]}
                        onChange={(e) => UpdateUsersRole(!userObject[roleId])}
                    ></input>
                    <span className='Checkbox'></span>
                </label>

                {/* Error Icon */}
                <img className='w-[25px]' src={errorIcon} alt='error-icon'></img>

            </div>

        )

    }

    //------------------------------------------------------

}
